import { useCallback, useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getAssessmentSubjectDetails, getAssessmentSubjectDetailsForUserWithLimitedAccess } from 'Api/UcrmTprm/UcrmTprmApi';
import { PageBackground } from 'Components/Containers/PageBackground/PageBackground';
import { PageContent } from 'Components/Containers/PageContent/PageContent';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { LinkButtonToast, TextToast } from 'Components/Toast/Toast';
import { InherentRiskQuestionnaireForm, InherentRiskQuestionnaireFormProps, InherentRiskQuestionnaireUserTypeAndData } from 'Components/UcrmTprm/InherentRiskQuestionnaireForm/InherentRiskQuestionnaireForm';
import { GENERIC_ERROR_MESSAGE, UNAUTHORIZED_MESSAGE } from 'Config/Errors';
import { TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { isForbiddenResponseError } from 'Helpers/Auth/ResponseUtil';
import { getDueDiligenceModuleRoles } from 'Helpers/UserUtils';
import { FinalizationState } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import styles from './InherentRiskQuestionnaire.module.css';

interface UrlParams {
    service_id: string;
    third_party_id: string;
}

export interface InherentRiskQuestionnaireProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

/** Renders the IRQ page (TPRM or UCRM, per `module`), accessible from the assessment subject's dashboard (when finalized) or from the organizations and assessment subjects listing (when a draft). */
export const InherentRiskQuestionnaire = (props: InherentRiskQuestionnaireProps) => {
    const params = useParams<keyof UrlParams>() as UrlParams;
    const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'failure'; message: string }>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);
    const [tprmAccessDenied, setTprmAccessDenied] = useState<boolean>();
    const [userTypeAndData, setUserTypeAndData] = useState<InherentRiskQuestionnaireUserTypeAndData>();

    const getAssessmentSubjectAndAssessmentConfigurations = useCallback(async (): Promise<void> => {
        try {
            const userRoles = props.authHandler.getUserRoles();
            const userHasFullAccess = userHasAuthorizedRole(userRoles, getDueDiligenceModuleRoles(props.module));

            if (userHasFullAccess) {
                const assessmentSubject = (await getAssessmentSubjectDetails(props.moduleApi, params.third_party_id, params.service_id)).data;
                const defaultIrq = (await props.moduleApi.getInherentRiskQuestionnaire(params.third_party_id, params.service_id)).data!;
                const assessmentConfigurations = (await props.dueDiligenceAssessmentConfigurationApi.getAllAssessmentConfigurations()).data;
                setUserTypeAndData({ userHasFullAccess: true, historical: false, assessmentConfigurations, defaultIrq, assessmentSubject });
            } else {
                const assessmentSubject = (await getAssessmentSubjectDetailsForUserWithLimitedAccess(props.moduleApi, params.third_party_id, params.service_id)).data;
                const defaultIrq = (await props.moduleApi.getInherentRiskQuestionnaire(params.third_party_id, params.service_id)).data!;
                setUserTypeAndData({ userHasFullAccess: false, historical: false, defaultIrq, assessmentSubject });
            }
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                console.log(error);
                setTprmAccessDenied(true);
            } else {
                setLoadingErrorOccurred(true);
            }
        }
    }, [props.authHandler, props.module, props.moduleApi, props.dueDiligenceAssessmentConfigurationApi, params.third_party_id, params.service_id]);

    useEffect(() => {
        getAssessmentSubjectAndAssessmentConfigurations();
    }, [getAssessmentSubjectAndAssessmentConfigurations]);

    if (tprmAccessDenied) {
        return (
            <PageBackground color="white">
                <PageContent>
                    <div className={styles.zeroStateContainer}>
                        <Text>{UNAUTHORIZED_MESSAGE}</Text>
                    </div>
                </PageContent>
            </PageBackground>
        );
    }

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    }

    if (!userTypeAndData) {
        return <Placeholder />;
    }

    const inherentRiskQuestionnaireFormProps: InherentRiskQuestionnaireFormProps = {
        artificialIntelligenceApi: props.artificialIntelligenceApi,
        documentApi: props.documentApi,
        module: props.module,
        onFormSubmitBegin: () => setToastMessage(undefined),
        onFormSubmitFailed: (message) => {
            setToastMessage({ type: 'failure', message });
        },
        onFormSubmitSucceeded: async () => {
            setToastMessage({ type: 'success', message: 'Inherent risk questionnaire saved.' });
            // WARNING: Like many places in the app, there is a race condition where stale assessment subject details may be fetched from DynamoDB, which means sometimes the page will not show the IRQ as having been submitted after a successful submission.
            getAssessmentSubjectAndAssessmentConfigurations();
        },
        moduleApi: props.moduleApi,
        userTypeAndData,
    };

    return (
        <>
            {toastMessage && toastMessage.type === 'failure' && <TextToast variant={toastMessage.type} clearToast={() => setToastMessage(undefined)} text={toastMessage.message} />}
            {toastMessage && toastMessage.type === 'success' && <LinkButtonToast variant={toastMessage.type} clearToast={() => setToastMessage(undefined)} text={toastMessage.message} linkButtonText={props.module === ProductModule.UCRM ? 'Return to use case' : 'Return to third-party service'} linkButtonTo={generatePath(props.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: params.third_party_id, service_id: params.service_id })} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING)}>{props.module}</BreadcrumbLink>
                        {userTypeAndData.assessmentSubject.finalization_state === FinalizationState.FINALIZED && <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: userTypeAndData.assessmentSubject.vendor_id, service_id: userTypeAndData.assessmentSubject.id })}>{`${userTypeAndData.assessmentSubject.vendor_name} - ${userTypeAndData.assessmentSubject.name}`}</BreadcrumbLink>}
                        <BreadcrumbText>Inherent Risk Questionnaire</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Inherent Risk Questionnaire"
                headerDescription={`The recommended inherent risk rating will be updated as questions are answered. The inherent risk rating drives prioritization of controls to reduce organizational risk. Questions are based on the inherent risk questionnaire configuration at the time when this ${props.module === ProductModule.UCRM ? "use case's" : "third-party service's"} due diligence workflow was started, and therefore may differ from the current inherent risk questionnaire configuration.`}
                body={[
                    {
                        content: <InherentRiskQuestionnaireForm {...inherentRiskQuestionnaireFormProps} />,
                    },
                ]}
            />
        </>
    );
};
